import { useMemo } from 'react';

import { useDiscountProductsQuery } from 'src/app/queries/discounts/get-discount-products';

import type { DiscountProduct } from 'src/app/queries/discounts/get-discount-products';
import type { Product } from 'src/app/queries/insights/inventory/get-location-products';

// Type for product resources
export type ProductResource = {
  ProductId: number | string;
  ProductName: string;
};

/**
 * Filters discount products using the same logic as the wizard's internal filtering.
 * Excludes products that are retired AND have no available inventory.
 *
 * This ensures that products passed to initial state match what the wizard will display,
 * preventing mismatches between selected count and eligible count.
 *
 * Algorithm:
 * 1. Validate input array is not null/undefined
 * 2. Filter out products with missing required fields:
 *    - ProductId must be a number
 *    - ProductDesc must be present
 * 3. Apply eligibility rules:
 *    - Include all non-retired products (IsRetired === false)
 *    - Include retired products ONLY if they have available inventory (Available > 0)
 *    - Exclude retired products with no inventory (IsRetired === true && Available === 0)
 * 4. Use nullish coalescing (??) to treat null/undefined Available as 0
 *
 * @param products - Array of discount products to filter
 * @returns Filtered array containing only eligible products
 *
 * @example
 * // Input: 10 discount products (2 retired with no stock, 1 missing ProductId)
 * // Output: 7 eligible products
 * const eligibleProducts = filterEligibleDiscountProducts(discountProducts);
 * const productFilter = createProductFilter(eligibleProducts);
 */
export function filterEligibleDiscountProducts(products: DiscountProduct[]): DiscountProduct[] {
  if (!products || !Array.isArray(products)) {
    return [];
  }

  return products.filter((product) => {
    // Skip products with missing required fields
    if (!product || typeof product.ProductId !== 'number' || !product.ProductDesc) {
      return false;
    }

    // Filter out products that are retired AND have no available inventory
    return !product.IsRetired || (product.Available ?? 0) > 0;
  });
}

/**
 * Matches inventory products with discount products and returns them in ProductResource format.
 * This handles the cross-reference between inventory insights products and the discount system.
 *
 * Algorithm:
 * 1. Validate both input arrays are not null/undefined
 * 2. Filter discount products for eligibility using filterEligibleDiscountProducts:
 *    - Removes retired products with no inventory
 *    - Removes products with missing required fields
 * 3. Extract valid ProductIds from inventory products:
 *    - Filter out null/undefined ProductIds
 *    - Convert string ProductIds to numbers (type coercion from API)
 * 4. Match discount products with inventory by ProductId:
 *    - Only include discount products that exist in inventory
 *    - Skip discount products with null ProductId
 * 5. Map matched products to ProductResource format:
 *    - ProductId: from discount product (number)
 *    - ProductName: from discount product's ProductDesc field
 *
 * @param inventoryProducts - Products from inventory insights API (ProductId may be string at runtime)
 * @param discountProducts - Products from discount system API (ProductId is number)
 * @returns Matched products in ProductResource format, ready for filter creation
 *
 * @example
 * // Input: 50 inventory products, 100 discount products (20 retired, 5 missing fields)
 * // Output: 45 matched eligible products
 * const { data: discountProducts } = useDiscountProductsQuery();
 * const matchedProducts = matchInventoryProductsWithDiscountProducts(products, discountProducts);
 * const productFilter = createProductFilter(matchedProducts);
 */
export function matchInventoryProductsWithDiscountProducts(
  inventoryProducts: Product[],
  discountProducts: DiscountProduct[]
): ProductResource[] {
  // Validate inputs
  if (!inventoryProducts || !Array.isArray(inventoryProducts)) {
    return [];
  }

  if (!discountProducts || !Array.isArray(discountProducts)) {
    return [];
  }

  // Filter discount products to only include eligible ones (not retired or has availability)
  const eligibleDiscountProducts = filterEligibleDiscountProducts(discountProducts);

  // Match discount products with inventory products by ProductId
  // Note: The inventory type definition says ProductId is a string, but at runtime it's actually a number
  const inventoryProductIDs = inventoryProducts
    .filter((product) => product?.ProductId != null)
    .map((product) => Number(product.ProductId));

  const matchedDiscountProducts = eligibleDiscountProducts.filter(
    (dp) => dp?.ProductId != null && inventoryProductIDs.includes(dp.ProductId)
  );

  // Map DiscountProduct to ProductResource format
  // DiscountProduct uses ProductDesc, but ProductResource expects ProductName
  return matchedDiscountProducts.map((dp) => ({
    ProductId: dp.ProductId,
    ProductName: dp.ProductDesc,
  }));
}

/**
 * Custom hook that fetches discount products and matches them with inventory products.
 * This simplifies the call site by combining data fetching and matching logic.
 *
 * Algorithm:
 * 1. Fetch discount products from API using useDiscountProductsQuery:
 *    - Query includes SelectedLspId for proper cache invalidation
 *    - Returns undefined while loading
 * 2. Memoize the matching process to avoid unnecessary recalculations:
 *    - Only recalculates when inventoryProducts or discountProducts change
 *    - Returns empty array if discount products haven't loaded yet
 * 3. Match and filter using matchInventoryProductsWithDiscountProducts:
 *    - Cross-references inventory and discount systems
 *    - Filters for eligibility (not retired or has inventory)
 *    - Maps to ProductResource format
 *
 * @param inventoryProducts - Products from inventory insights API
 * @param lspId - Location Service Provider ID for fetching discount products (optional)
 * @returns Matched products in ProductResource format, ready for createProductFilter
 *
 * @example
 * // Input: 100 inventory products, fetches discount products for lspId
 * // Output: 85 matched eligible products (15 filtered as retired/inactive)
 * const matchedProducts = useMatchedInventoryProducts(products, lspId);
 * const productFilter = createProductFilter(matchedProducts);
 */
export function useMatchedInventoryProducts(inventoryProducts: Product[], lspId?: number): ProductResource[] {
  const { data: discountProducts } = useDiscountProductsQuery({ SelectedLspId: lspId });

  return useMemo(() => {
    if (!discountProducts) {
      return [];
    }

    return matchInventoryProductsWithDiscountProducts(inventoryProducts, discountProducts);
  }, [inventoryProducts, discountProducts]);
}
